// Automations for a location — When <sensor event> · Only if … · Do <buttons>.
//
// One dialog, three doors in:
//   • the location (SecurityBar → Automations)       every rule
//   • a sensor (RfSensorDialog → Automations)        `forSensor`: its rules; Add starts from it
//   • a light (ButtonSettingsDialog → Paired with)   `forTarget`: rules that switch it; Add targets it
//
// The editor is checkboxes and dropdowns only — a condition that is not ticked
// does not apply. Run by the server (Home Automation api/new/automations.js);
// wording from core/automations/automationText.js.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  TextField, Switch, IconButton, Checkbox, FormControlLabel, LinearProgress, Tooltip, Divider,
  ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import { useApp } from '../../core/state/AppContext';
import {
  getAutomations, saveAutomation, setAutomationEnabled, deleteAutomation, getAutomationLog,
} from '../../core/api/api';
import {
  describeAutomation, newAutomation, validateAutomation, DAY_NAMES, toggleDay, COMMANDS, FAN_STEPS,
} from '../../core/automations/automationText';
import { rfType, rfEventChoices } from '../../core/devices/rfTypes';
import { fitDialog, PHONE_SHEET } from './fitDialog';
import VacancyPanel from './VacancyPanel';

const hm = (t) => (t ? String(t).slice(0, 5) : '');
const targetKey = (t) => `${t.UniqueNumber}|${t.Output}`;

// ── The editor ─────────────────────────────────────────────────────────────
function Editor({ value, ctx, onCancel, onSaved }) {
  const { userId } = useApp();
  const [a, setA] = useState(value);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const sensor = ctx.sensors.find((s) => Number(s.RfDeviceId) === Number(a.TriggerRfDeviceId));
  const events = sensor ? ((sensor.Events && sensor.Events.length) ? sensor.Events : rfEventChoices(sensor.RfType)) : [];
  const timeOn = !!(a.TimeFrom || a.TimeTo);

  const save = async () => {
    const why = validateAutomation(a);
    if (why) { setError(why); return; }
    setBusy(true); setError('');
    try {
      const r = await saveAutomation(userId, { ...a, TimeFrom: hm(a.TimeFrom), TimeTo: hm(a.TimeTo) }, a.Actions);
      if (r?.data?.ok === false) throw new Error(r.data.error);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || (e?.response?.status === 404
        ? 'The server does not have automations yet — deploy the backend and run automation_migration.sql.'
        : `Could not save (${e.message}).`));
    } finally {
      setBusy(false);
    }
  };

  const setAction = (i, patch) => set('Actions', a.Actions.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <Box>
      <TextField size="small" label="Name (optional)" value={a.Name} onChange={(e) => set('Name', e.target.value)}
        placeholder="Night lamp when the door opens" sx={{ mb: 1.5, width: 340, maxWidth: '100%' }} />

      {/* ── When ── */}
      <Typography variant="overline" color="text.secondary">When</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={a.TriggerKind} onChange={(_, v) => v && set('TriggerKind', v)}>
          <ToggleButton value="rf">RF sensor</ToggleButton>
          <ToggleButton value="message">Wired / device message</ToggleButton>
        </ToggleButtonGroup>
        {a.TriggerKind === 'rf' ? (
          <>
            <TextField select size="small" label="Sensor" value={Number(a.TriggerRfDeviceId) || ''} sx={{ minWidth: 200 }}
              onChange={(e) => setA((x) => ({ ...x, TriggerRfDeviceId: Number(e.target.value), TriggerMeaning: '' }))}>
              {ctx.sensors.map((s) => (
                <MenuItem key={s.RfDeviceId} value={Number(s.RfDeviceId)}>
                  {`${rfType(s.RfType).icon} ${s.Name}${s.RoomName ? ` · ${s.RoomName}` : ''}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Event" value={a.TriggerMeaning} sx={{ minWidth: 130 }}
              onChange={(e) => set('TriggerMeaning', e.target.value)} disabled={!sensor}>
              <MenuItem value="">Any event</MenuItem>
              {events.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </>
        ) : (
          <>
            <TextField select size="small" label="Device" value={a.TriggerUniqueNumber} sx={{ minWidth: 200 }}
              onChange={(e) => set('TriggerUniqueNumber', e.target.value)}>
              {ctx.messageDevices.map((d) => (
                <MenuItem key={`${d.UniqueNumber}-${d.InputName}`} value={String(d.UniqueNumber)}>
                  {`${d.DeviceName || d.UniqueNumber}${d.InputName ? ` — ${d.InputName}` : ''}${d.RoomName ? ` · ${d.RoomName}` : ''}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Its message contains" value={a.TriggerText}
              onChange={(e) => set('TriggerText', e.target.value)} placeholder="motion detected" />
          </>
        )}
      </Box>

      {/* ── Only if ── */}
      <Typography variant="overline" color="text.secondary">Only if (ticked ones must all hold)</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel sx={{ minWidth: 150 }} label="Between"
            control={<Checkbox size="small" checked={timeOn}
              onChange={(e) => setA((x) => ({ ...x, TimeFrom: e.target.checked ? '19:00' : '', TimeTo: e.target.checked ? '00:00' : '' }))} />} />
          <TextField size="small" type="time" value={hm(a.TimeFrom)} disabled={!timeOn} onChange={(e) => set('TimeFrom', e.target.value)} />
          <Typography variant="body2">and</Typography>
          <TextField size="small" type="time" value={hm(a.TimeTo)} disabled={!timeOn} onChange={(e) => set('TimeTo', e.target.value)} />
          {timeOn && <Typography variant="caption" color="text.secondary">crossing midnight is fine</Typography>}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel sx={{ minWidth: 150 }} label="On days"
            control={<Checkbox size="small" checked={!!Number(a.Days)} onChange={(e) => set('Days', e.target.checked ? 62 : 0)} />} />
          {DAY_NAMES.map((d, i) => (
            <Button key={d} size="small" disabled={!Number(a.Days)}
              variant={Number(a.Days) & (1 << i) ? 'contained' : 'outlined'}
              onClick={() => set('Days', toggleDay(a.Days, i) || 0)} sx={{ minWidth: 40, px: 0.5 }}>
              {d}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField select size="small" label="Premises" value={a.ModeIs} sx={{ minWidth: 170 }} onChange={(e) => set('ModeIs', e.target.value)}>
            <MenuItem value="">Locked or unlocked</MenuItem>
            <MenuItem value="unlocked">🔓 Only when unlocked</MenuItem>
            <MenuItem value="locked">🔒 Only when locked</MenuItem>
          </TextField>
          <TextField select size="small" label="Room" value={a.OccupancyIs} sx={{ minWidth: 170 }} onChange={(e) => set('OccupancyIs', e.target.value)}>
            <MenuItem value="">Anyone or no one in</MenuItem>
            <MenuItem value="occupied">Only if someone is in</MenuItem>
            <MenuItem value="empty">Only if it is empty</MenuItem>
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel label="Only if the light is OFF"
            control={<Checkbox size="small" checked={!!Number(a.OnlyIfOff)} onChange={(e) => set('OnlyIfOff', e.target.checked ? 1 : 0)} />} />
          <FormControlLabel label="Not again within"
            control={<Checkbox size="small" checked={Number(a.CooldownMin) > 0} onChange={(e) => set('CooldownMin', e.target.checked ? 5 : 0)} />} />
          <TextField size="small" type="number" value={a.CooldownMin} disabled={!(Number(a.CooldownMin) > 0)} sx={{ width: 80 }}
            onChange={(e) => set('CooldownMin', Math.max(0, Number(e.target.value) || 0))} inputProps={{ min: 0 }} />
          <Typography variant="body2">min</Typography>
        </Box>
      </Box>

      {/* ── Do ── */}
      <Typography variant="overline" color="text.secondary">Do</Typography>
      {a.Actions.map((x, i) => {
        const t = ctx.targets.find((y) => targetKey(y) === targetKey(x));
        const isFan = t && Number(t.Dimming) > 0;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Button" value={x.UniqueNumber ? targetKey(x) : ''} sx={{ minWidth: 260 }}
              onChange={(e) => { const [u, o] = e.target.value.split('|'); setAction(i, { UniqueNumber: u, Output: Number(o) }); }}>
              {ctx.targets.map((y) => (
                <MenuItem key={targetKey(y)} value={targetKey(y)}>
                  {`${y.OutputName || `#${y.Output}`} — ${y.DeviceName || y.UniqueNumber}${y.RoomName ? ` · ${y.RoomName}` : ''}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Turn" value={x.Command} sx={{ minWidth: 110 }} onChange={(e) => setAction(i, { Command: e.target.value })}>
              {COMMANDS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              {isFan && FAN_STEPS.map((s) => <MenuItem key={s} value={s}>{`Fan ${s}%`}</MenuItem>)}
            </TextField>
            <IconButton size="small" onClick={() => set('Actions', a.Actions.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Box>
        );
      })}
      <Button size="small" startIcon={<AddIcon />} onClick={() => set('Actions', [...a.Actions, { UniqueNumber: '', Output: 0, Command: 'on' }])}>
        Add a button
      </Button>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
        <FormControlLabel label="Then turn them OFF again after"
          control={<Checkbox size="small" checked={Number(a.OffAfterMin) > 0} onChange={(e) => set('OffAfterMin', e.target.checked ? 10 : 0)} />} />
        <TextField size="small" type="number" value={a.OffAfterMin} disabled={!(Number(a.OffAfterMin) > 0)} sx={{ width: 80 }}
          onChange={(e) => set('OffAfterMin', Math.max(0, Number(e.target.value) || 0))} inputProps={{ min: 0 }} />
        <Typography variant="body2">min</Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 1.5 }}>
        {describeAutomation({ ...a, Actions: a.Actions.map((x) => ({ ...x, ...(ctx.targets.find((y) => targetKey(y) === targetKey(x)) || {}) })) }, ctx)}
      </Alert>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {/* Pinned, so Save never sits below the fold. */}
      <Box sx={{
        position: 'sticky', bottom: -16, zIndex: 2, bgcolor: 'background.paper',
        display: 'flex', justifyContent: 'flex-end', gap: 1, py: 1, mt: 1, borderTop: 1, borderColor: 'divider',
      }}
      >
        <Button onClick={onCancel} disabled={busy}>Back</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Save automation</Button>
      </Box>
    </Box>
  );
}

// ── The list ───────────────────────────────────────────────────────────────
/**
 * @param locationId
 * @param forSensor  { rfDeviceId, meaning? }   only rules triggered by it; Add starts from it
 * @param forTarget  { uniqueNumber, output }   only rules that switch it; Add targets it
 */
export default function AutomationsDialog({
  open, onClose, locationId, locationName = '', forSensor = null, forTarget = null,
}) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const { userId } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [logFor, setLogFor] = useState(null);   // { id, rows }
  const [vacEditing, setVacEditing] = useState(false);

  const load = useCallback(() => {
    if (!locationId) return;
    getAutomations(locationId, userId)
      .then((r) => { if (r?.data?.ok === false) throw new Error(r.data.error); setData(r.data); setError(''); })
      .catch((e) => setError(e?.response?.status === 404
        ? 'The server does not have automations yet — deploy the backend and run automation_migration.sql.'
        : `Could not load automations (${e.message}).`));
  }, [locationId, userId]);

  useEffect(() => { if (open) { setEditing(null); setLogFor(null); setVacEditing(false); load(); } }, [open, load]);

  const ctx = useMemo(() => ({
    sensors: (data && data.sensors) || [],
    messageDevices: (data && data.messageDevices) || [],
    targets: (data && data.targets) || [],
  }), [data]);

  const list = ((data && data.automations) || []).filter((r) => {
    if (forSensor) return Number(r.TriggerRfDeviceId) === Number(forSensor.rfDeviceId);
    if (forTarget) {
      return (r.Actions || []).some((x) => String(x.UniqueNumber) === String(forTarget.uniqueNumber)
        && Number(x.Output) === Number(forTarget.output));
    }
    return true;
  });

  const startNew = () => setEditing(newAutomation({
    locationId,
    rfDeviceId: forSensor ? forSensor.rfDeviceId : 0,
    meaning: forSensor ? (forSensor.meaning || '') : '',
    uniqueNumber: forTarget ? forTarget.uniqueNumber : '',
    output: forTarget ? forTarget.output : 0,
  }));

  return (
    <Dialog open={!!open} onClose={onClose} {...(narrow ? PHONE_SHEET : fitDialog({}, 'sm'))}>
      <DialogTitle sx={{ pb: 0.5 }}>
        {editing ? (editing.AutomationId ? 'Edit automation' : 'New automation') : `Automations${locationName ? ` — ${locationName}` : ''}`}
        {!editing && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            When a sensor does something, switch lights and fans — with conditions. Runs on the server, phone or not.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
        {!data && !error && <LinearProgress />}
        {data && editing && (
          <Editor value={editing} ctx={ctx} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        )}
        {data && !editing && (
          <>
            {!vacEditing && !list.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {forTarget ? 'Nothing switches this button automatically yet.' : 'No automations yet.'}
              </Typography>
            )}
            {!vacEditing && list.map((r) => (
              <Box key={r.AutomationId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Switch size="small" checked={!!Number(r.Enabled)}
                  onChange={async (e) => { await setAutomationEnabled(r.AutomationId, e.target.checked, userId).catch(() => {}); load(); }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {r.Name && <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.Name}</Typography>}
                  <Typography variant="body2" color={Number(r.Enabled) ? 'text.primary' : 'text.secondary'}>
                    {describeAutomation(r, ctx)}
                  </Typography>
                  {r.LastFiredAt && (
                    <Typography variant="caption" color="text.secondary">{`last ran ${String(r.LastFiredAt).replace('T', ' ').slice(0, 16)}`}</Typography>
                  )}
                </Box>
                <Tooltip title="What it did, and why it didn't">
                  <IconButton size="small" onClick={async () => {
                    const res = await getAutomationLog(r.AutomationId, userId).catch(() => null);
                    setLogFor({ id: r.AutomationId, rows: (res && res.data && res.data.log) || [] });
                  }}><HistoryIcon fontSize="small" /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setEditing({
                  ...r, TimeFrom: hm(r.TimeFrom), TimeTo: hm(r.TimeTo),
                  Actions: (r.Actions || []).map((x) => ({ UniqueNumber: String(x.UniqueNumber), Output: Number(x.Output), Command: x.Command })),
                })}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (!window.confirm('Delete this automation?')) return;
                  await deleteAutomation(r.AutomationId, userId).catch(() => {});
                  load();
                }}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            {/* Room-level: door closed + no motion → all OFF. Only on the
                location's full list — not when opened for one sensor or light. */}
            {!forSensor && !forTarget && <VacancyPanel locationId={locationId} ctx={ctx} onEditingChange={setVacEditing} />}
            {!vacEditing && logFor && (
              <>
                <Divider sx={{ my: 1 }}>Last decisions</Divider>
                {!logFor.rows.length && <Typography variant="caption" color="text.secondary">Nothing yet.</Typography>}
                {logFor.rows.map((l, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                    {`${String(l.At).replace('T', ' ').slice(0, 19)}  ${Number(l.Fired) ? '✔ ran' : '· skipped'}  ${l.Note}`}
                  </Typography>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
      {!editing && (
        <DialogActions>
          <Button startIcon={<AddIcon />} onClick={startNew} disabled={!data}>New automation</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
